import { useContext } from 'react';

import { L10nContext } from 'context/L10nContext';

import { CardItemTagType } from 'presentation/ui/partials/card/CardItemTagType';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { Illustration } from 'presentation/ui/partials/illustration/Illustration';
import { IllustrationIdentifier } from 'presentation/ui/partials/illustration/IllustrationIdentifier';
import { Tag, TagType } from 'presentation/ui/partials/tag/Tag';
import { GeneratorModelState } from 'services/nuclide/domain/model/GeneratorModelState';

import './card-item-generator-header.scss';

export interface CardItemGeneratorHeaderProps {
	/**
	 * Title
	 */
	title: string;
	/**
	 * (Optional) Sub-Title
	 */
	subtitle?: string | Date;
	/**
	 * (Optional) Illustration
	 */
	illustration?: IllustrationIdentifier
	/**
	 * (Optional) Tag
	 */
	tag?: CardItemTagType
	/**
	 * (Optional) Info-Tag
	 */
	infoTag?: CardItemTagType
	/**
	 * (Optional) Archived tag
	 */
	state?: GeneratorModelState;
}

/**
 * A card item header component
 */
export const CardItemGeneratorHeader = (props: CardItemGeneratorHeaderProps): JSX.Element => {
	const { title, subtitle, illustration, tag, infoTag, state } = props;

	const l10nContext = useContext(L10nContext);

	const illuElement = illustration ?
		<Illustration name={illustration} /> :
		null;

	const subTitle = subtitle ?
		<p className='card-item-generator-header__subtitle'>
			{subtitle}
		</p> :
		null;

	const tagElement = tag ?
		<Tag
			label={tag.label}
			type={tag.type}
		/> :
		null;

	const infoTagElement = infoTag ?
		<Tag
			label={infoTag.label}
			type={infoTag.type}
			icon={infoTag.icon}
		/> :
		null;

	const statusTagElement = (): JSX.Element => {
		const tagLabelDispatched = l10nContext.translate('common.tags.dispatched', 'Abgegangen');

		if (state === GeneratorModelState.DISPATCHED) {
			return (
				<Tag
					label={tagLabelDispatched}
					type={TagType.INFO}
					icon={IconIdentifier.DISPATCH}
				/>
			);
		}

		return null;
	};

	return (
		<div className={`card-item-generator-header${ !illustration ? ' no-illustration' : ''}`}>
			{illuElement}

			<div className='card-item-generator-header__content'>
				<span className='card-item-generator-header__title-wrapper'>
					<h1 className='card-item-generator-header__title'>
						{title}
					</h1>
					{tagElement}
				</span>

				{subTitle}
			</div>

			{infoTagElement}
			{statusTagElement()}
		</div>
	);
};
